import React from "react";
import {Ball} from "./Ball";
import {OverlayTrigger, Popover} from "react-bootstrap";
import * as PropTypes from "prop-types";

/**
 * Pfam domain ID to color mappings (standard across all proteins)
 * Colors based on domain function/family for consistency with literature
 */
const PFAM_COLORS = {
  // Kinase domains
  'PF00069': '#E74C3C',  // Protein kinase domain
  'PF07714': '#C0392B',  // Protein tyrosine kinase
  'PF14531': '#E74C3C',  // Kinase-like

  // Signaling domains
  'PF00017': '#3498DB',  // SH2 domain
  'PF00018': '#9B59B6',  // SH3 domain
  'PF00169': '#1ABC9C',  // PH domain
  'PF00621': '#16A085',  // RhoGEF domain
  'PF00620': '#27AE60',  // RhoGAP domain

  // Phosphatases
  'PF00102': '#E67E22',  // Protein-tyrosine phosphatase
  'PF00481': '#D35400',  // Protein phosphatase 2C

  // GTPases
  'PF00071': '#F39C12',  // Ras family
  'PF00025': '#F1C40F',  // ADP-ribosylation factor family

  // DNA/RNA binding
  'PF00096': '#2ECC71',  // Zinc finger, C2H2 type
  'PF00104': '#27AE60',  // Ligand-binding domain of nuclear hormone receptor
  'PF00098': '#229954',  // Zinc knuckle

  // Immunoglobulin-like
  'PF00047': '#16A085',  // Immunoglobulin domain
  'PF07679': '#1ABC9C',  // Immunoglobulin I-set domain

  // Transmembrane
  'PF00001': '#F39C12',  // 7 transmembrane receptor
  'PF01352': '#F1C40F',  // GPCR family 2

  // Enzyme domains
  'PF00004': '#8E44AD',  // ATPase family
  'PF00005': '#9B59B6',  // ABC transporter

  // Default fallback
  'default': '#BDC3C7'
};

/**
 * Name-based color fallback for domains without Pfam IDs
 */
const DOMAIN_NAME_COLORS = {
  'kinase': '#E74C3C',
  'sh2': '#3498DB',
  'sh3': '#9B59B6',
  'ph': '#1ABC9C',
  'phosphatase': '#E67E22',
  'gtpase': '#F39C12',
  'atpase': '#D35400',
  'zinc_finger': '#95A5A6',
  'helix': '#7F8C8D',
  'immunoglobulin': '#2ECC71',
  'egf': '#16A085',
  'fibronectin': '#27AE60',
  'lectin': '#8E44AD',
  'transmembrane': '#F39C12',
  'signal': '#3498DB',
  'default': '#BDC3C7'
};

const PROTEIN_CLASS_COLORS = {
  'kinase': '#E74C3C',
  'phosphatase': '#E67E22',
  'transcription_factor': '#9B59B6',
  'receptor': '#3498DB',
  'transporter': '#1ABC9C',
  'enzyme': '#F39C12',
  'channel': '#16A085',
  'protease': '#C0392B',
  'structural': '#7F8C8D',
  'signaling': '#2ECC71',
  'default': '#95A5A6'
};

/**
 * Domain type classification (for Pfam shape conventions)
 */
const DOMAIN_TYPES = {
  STANDARD: 'standard',           // Regular domain → Rectangle
  TRANSMEMBRANE: 'transmembrane', // TM domain → Diamond
  SIGNAL: 'signal',               // Signal peptide → Triangle
  DISORDERED: 'disordered',       // Disordered region → Oval
  COILED_COIL: 'coiled_coil',    // Coiled-coil → Pentagon
  LOW_COMPLEXITY: 'low_complexity' // Low complexity → Hexagon
};

/**
 * Detect domain type from name/description (for shape selection)
 */
const detectDomainType = (domain) => {
  if (!domain) return DOMAIN_TYPES.STANDARD;

  const name = (domain.name || '').toLowerCase();
  const desc = (domain.description || '').toLowerCase();
  const combined = name + ' ' + desc;

  if (combined.includes('transmembrane') || combined.includes('tm domain')) {
    return DOMAIN_TYPES.TRANSMEMBRANE;
  }
  if (combined.includes('signal') || name === 'signal peptide') {
    return DOMAIN_TYPES.SIGNAL;
  }
  if (combined.includes('disorder') || combined.includes('intrinsically')) {
    return DOMAIN_TYPES.DISORDERED;
  }
  if (combined.includes('coiled') || combined.includes('coil')) {
    return DOMAIN_TYPES.COILED_COIL;
  }
  if (combined.includes('low complexity')) {
    return DOMAIN_TYPES.LOW_COMPLEXITY;
  }

  return DOMAIN_TYPES.STANDARD;
};

/**
 * Get color for a domain based on Pfam ID or name
 */
const getDomainColor = (domain) => {
  if (!domain) return PFAM_COLORS.default;

  // Priority 1: Use Pfam ID if available
  if (domain.pfam_id && PFAM_COLORS[domain.pfam_id]) {
    return PFAM_COLORS[domain.pfam_id];
  }

  // Priority 2: Use name-based color
  if (domain.name) {
    const normalized = domain.name.toLowerCase().replace(/[^a-z]/g, '_');
    if (DOMAIN_NAME_COLORS[normalized]) {
      return DOMAIN_NAME_COLORS[normalized];
    }
  }

  // Priority 3: Default
  return DOMAIN_NAME_COLORS.default;
};

/**
 * Protein class icon paths (SVG) - simple recognizable symbols
 */
const PROTEIN_CLASS_ICONS = {
  kinase: (size) => `M${size/2} ${size*0.2}L${size*0.8} ${size/2}L${size/2} ${size*0.8}L${size*0.2} ${size/2}Z M${size/2} ${size*0.35}L${size*0.65} ${size/2}L${size/2} ${size*0.65}L${size*0.35} ${size/2}Z`, // Diamond with inner diamond
  phosphatase: (size) => `M${size*0.3} ${size*0.3}L${size*0.7} ${size*0.3}L${size*0.7} ${size*0.7}L${size*0.3} ${size*0.7}Z M${size*0.4} ${size*0.5}L${size*0.6} ${size*0.5}`, // Square with minus
  receptor: (size) => `M${size/2} ${size*0.2}L${size/2} ${size*0.8} M${size*0.3} ${size*0.4}L${size*0.7} ${size*0.4} M${size*0.3} ${size*0.6}L${size*0.7} ${size*0.6}`, // Antenna shape
  transporter: (size) => `M${size*0.2} ${size*0.3}L${size*0.8} ${size*0.3}L${size*0.8} ${size*0.7}L${size*0.2} ${size*0.7}Z M${size*0.35} ${size*0.3}L${size*0.35} ${size*0.7} M${size*0.65} ${size*0.3}L${size*0.65} ${size*0.7}`, // Channels
  transcription_factor: (size) => `M${size*0.3} ${size*0.5}L${size*0.5} ${size*0.3}L${size*0.7} ${size*0.5}L${size*0.5} ${size*0.7}Z`, // DNA helix representation
  channel: (size) => `M${size*0.2} ${size*0.2}L${size*0.8} ${size*0.2}L${size*0.8} ${size*0.8}L${size*0.2} ${size*0.8}Z M${size/2} ${size*0.2}L${size/2} ${size*0.8}`, // Pore
  enzyme: (size) => `M${size/2} ${size*0.2}A${size*0.3} ${size*0.3} 0 1 1 ${size/2} ${size*0.8}A${size*0.3} ${size*0.3} 0 1 1 ${size/2} ${size*0.2}Z`, // Circular (generic enzyme)
  protease: (size) => `M${size*0.2} ${size*0.5}L${size*0.8} ${size*0.5} M${size*0.5} ${size*0.2}L${size*0.5} ${size*0.8}`, // Scissors representation
};

/**
 * Extract PDB ID from entity aliases for optional structure image
 */
const extractPDBId = (entity) => {
  if (entity.aliases && Array.isArray(entity.aliases)) {
    const pdbPattern = /^[0-9][A-Z0-9]{3}$/i;
    const pdbId = entity.aliases.find(alias =>
      typeof alias === 'string' && alias.length === 4 && pdbPattern.test(alias)
    );
    if (pdbId) {
      return pdbId.toLowerCase();
    }
  }
  return null;
};

/**
 * Render domain architecture as SVG
 */
const DomainArchitecture = ({ domains, proteinLength, scale, className }) => {
  if (!domains || domains.length === 0 || !proteinLength) {
    return null;
  }

  // Dimensions based on scale
  const dimensions = {
    sm: { width: 80, height: 20, fontSize: 6 },
    md: { width: 120, height: 30, fontSize: 8 },
    lg: { width: 160, height: 40, fontSize: 10 }
  };

  const { width, height, fontSize } = dimensions[scale] || dimensions.lg;
  const padding = 2;
  const barHeight = height - (padding * 2);

  return (
    <svg
      width={width}
      height={height}
      className={`protein-domain-architecture ${className || ''}`}
      viewBox={`0 0 ${width} ${height}`}
    >
      {/* Background protein bar */}
      <rect
        x={padding}
        y={padding}
        width={width - (padding * 2)}
        height={barHeight}
        fill="#ECF0F1"
        stroke="#BDC3C7"
        strokeWidth="1"
        rx="2"
      />

      {/* Domain blocks with Pfam shape conventions */}
      {domains.map((domain, idx) => {
        const startPercent = (domain.start / proteinLength) * 100;
        const endPercent = (domain.end / proteinLength) * 100;
        const domainWidth = ((endPercent - startPercent) / 100) * (width - (padding * 2));
        const x = padding + (startPercent / 100) * (width - (padding * 2));
        const centerX = x + domainWidth / 2;
        const centerY = padding + barHeight / 2;
        const color = getDomainColor(domain);
        const domainType = detectDomainType(domain);

        // Render different shapes based on domain type (Pfam conventions)
        if (domainType === DOMAIN_TYPES.TRANSMEMBRANE && domainWidth > 5) {
          // Diamond shape for transmembrane domains
          const halfWidth = domainWidth / 2;
          const halfHeight = barHeight / 2;
          return (
            <g key={`domain-${idx}`}>
              <polygon
                points={`${centerX},${padding} ${x + domainWidth},${centerY} ${centerX},${padding + barHeight} ${x},${centerY}`}
                fill={color}
                stroke="#FFFFFF"
                strokeWidth="1"
              >
                <title>{`${domain.name} (${domain.start}-${domain.end}) [Transmembrane]`}</title>
              </polygon>
            </g>
          );
        } else if (domainType === DOMAIN_TYPES.SIGNAL && domainWidth > 3) {
          // Triangle for signal peptides
          return (
            <g key={`domain-${idx}`}>
              <polygon
                points={`${x},${padding + barHeight} ${x + domainWidth},${padding + barHeight} ${centerX},${padding}`}
                fill={color}
                stroke="#FFFFFF"
                strokeWidth="1"
              >
                <title>{`${domain.name} (${domain.start}-${domain.end}) [Signal peptide]`}</title>
              </polygon>
            </g>
          );
        } else if (domainType === DOMAIN_TYPES.DISORDERED && domainWidth > 4) {
          // Oval for disordered regions
          return (
            <g key={`domain-${idx}`}>
              <ellipse
                cx={centerX}
                cy={centerY}
                rx={domainWidth / 2}
                ry={barHeight / 2}
                fill={color}
                fillOpacity="0.5"
                stroke="#FFFFFF"
                strokeWidth="1"
              >
                <title>{`${domain.name} (${domain.start}-${domain.end}) [Disordered]`}</title>
              </ellipse>
            </g>
          );
        } else {
          // Rectangle for standard domains
          return (
            <g key={`domain-${idx}`}>
              <rect
                x={x}
                y={padding}
                width={domainWidth}
                height={barHeight}
                fill={color}
                stroke="#FFFFFF"
                strokeWidth="1"
                rx="2"
              >
                <title>{`${domain.name} (${domain.start}-${domain.end})`}</title>
              </rect>
            </g>
          );
        }
      })}
    </svg>
  );
};

/**
 * Render functional icon/ball with protein class styling
 */
const ProteinClassBall = ({ proteinClass, title, scale, className }) => {
  const color = PROTEIN_CLASS_COLORS[proteinClass] || PROTEIN_CLASS_COLORS.default;
  const iconPath = PROTEIN_CLASS_ICONS[proteinClass];

  // Dimensions based on scale
  const sizes = {
    sm: 30,
    md: 45,
    lg: 60
  };
  const size = sizes[scale] || sizes.lg;

  if (iconPath) {
    // Render SVG icon for protein class
    return (
      <svg
        width={size}
        height={size}
        className={`protein-class-icon ${proteinClass || 'default'}${className ? ' ' + className : ''}`}
        viewBox={`0 0 ${size} ${size}`}
        title={title}
      >
        <circle cx={size/2} cy={size/2} r={size/2 - 2} fill={color} opacity="0.9" />
        <path
          d={iconPath(size)}
          fill="white"
          stroke="white"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    );
  }

  // Fallback to text-based ball
  const text = proteinClass ? proteinClass.substring(0, 3).toUpperCase() : "PRO";
  return (
    <Ball
      title={title}
      text={text}
      scale={scale}
      className={`protein-class ${proteinClass || 'default'}${className ? ' ' + className : ''}`}
      style={{backgroundColor: color}}
    />
  );
};

const ProteinImage = ({
  entity = {},
  width = null,
  height = null,
  alt = null,
  className = null,
  popover = false,
  ordinal = null,
  title = "",
  scale = "lg",
  disableRotation = false
}) => {
  const pdbId = extractPDBId(entity);
  const hasDomains = entity.domains && Array.isArray(entity.domains) && entity.domains.length > 0;
  const hasProteinClass = entity.proteinClass;

  const renderContent = () => {
    // Priority 1: PDB structure image (replaces gray ball when available)
    if (pdbId) {
      return (
        <img
          className={`${className ? className + ' ' : ''}protein-structure-image`}
          src={`https://cdn.rcsb.org/images/structures/${pdbId}_assembly-1.jpeg`}
          alt={alt || title || entity.label}
          onError={(e) => {
            // Fallback to ball on image error
            e.target.style.display = 'none';
            if (e.target.nextSibling) {
              e.target.nextSibling.style.display = 'block';
            }
          }}
        />
      );
    }

    // Priority 2: Protein class styled ball
    if (hasProteinClass) {
      return (
        <ProteinClassBall
          proteinClass={entity.proteinClass}
          title={title || entity.categoryName || entity.label}
          scale={scale}
          className={className}
        />
      );
    }

    // Priority 3: Generic ball
    return (
      <Ball
        title={title || entity.categoryName}
        scale={scale}
        className={`category${className ? ' ' + className : ''}`}
      />
    );
  };

  const content = renderContent();

  if (popover) {
    return (
      <OverlayTrigger
        placement="right"
        overlay={
          <Popover id={`popup-protein-${ordinal}`} className="protein-popover">
            <div className="protein-popover-content">
              {hasDomains && entity.proteinLength && (
                <div className="protein-domains-detail">
                  <DomainArchitecture
                    domains={entity.domains}
                    proteinLength={entity.proteinLength}
                    scale="lg"
                    className="popover"
                  />
                  <div className="domain-list">
                    {entity.domains.map((domain, idx) => (
                      <div key={idx} className="domain-item">
                        <span
                          className="domain-color"
                          style={{backgroundColor: getDomainColor(domain)}}
                        />
                        <span className="domain-name">{domain.name}</span>
                        <span className="domain-range">{domain.start}-{domain.end}</span>
                        {domain.pfam_id && (
                          <span className="domain-pfam-id">({domain.pfam_id})</span>
                        )}
                      </div>
                    ))}
                  </div>                </div>
              )}
              {pdbId && (
                <div className="protein-structure-link">
                  <a
                    href={`https://www.rcsb.org/structure/${pdbId.toUpperCase()}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    View structure: {pdbId.toUpperCase()}
                  </a>
                </div>
              )}
            </div>
          </Popover>
        }
        defaultShow={false}>
        <div className="with-hover">
          {content}
        </div>
      </OverlayTrigger>
    );
  }

  return content;
};

ProteinImage.propTypes = {
  entity: PropTypes.object.isRequired,
  width: PropTypes.number,
  height: PropTypes.number,
  alt: PropTypes.string,
  className: PropTypes.string,
  popover: PropTypes.bool,
  ordinal: PropTypes.number,
  title: PropTypes.string,
  scale: PropTypes.string,
  disableRotation: PropTypes.bool,
};

// Export DomainArchitecture for use in search results
export { DomainArchitecture };

export default ProteinImage;
